/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */
import { useEffect, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { Link, useLocation, useParams } from "react-router-dom";
import { db, auth } from "../firebase";
import useScrollMemory from "../hooks/useScrollMemory";
import TaxonomyStepper from "../components/TaxonomyStepper";
import BioIndexExploreNavigation from "../components/BioIndexExploreNavigation";
import ExplorePhotoModes from "../components/ExplorePhotoModes";
import { iconUrl, defaultImg } from "../utils/icons";
import {
  kingdoms,
  phylaByKingdom,
  classesByPhylum,
  ordersByClass,
  familiesByOrder,
  generaByFamily,
} from "../utils/taxonomy";
import "../styles/BioIndexCommunityMobile.css";
import "../styles/BioIndexExplore.css";

const MODE_LABELS = {
  taxonomy: "Kingdoms",
  alpha: "Alphabetical browsing",
  date: "Observations by date",
  location: "Observations by location",
};

function ExploreSpecies() {
  useScrollMemory();

  const [speciesCounts, setSpeciesCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [browseMode, setBrowseMode] = useState("taxonomy");
  const stepperPanelRef = useRef(null);
  const { uid } = useParams();
  const location = useLocation();
  const currentUID = auth.currentUser?.uid;
  const dataUID = uid || currentUID;
  const isOwnCollection = !uid || uid === currentUID;
  const adminModeActive =
    !!uid && new URLSearchParams(location.search).get("adminMode") === "true";
  const adminModeSuffix = adminModeActive ? "?adminMode=true" : "";

  useEffect(() => {
    let cancelled = false;

    async function fetchSpeciesCounts() {
      setLoadingCounts(true);

      if (!dataUID) {
        setSpeciesCounts({});
        setLoadingCounts(false);
        return;
      }

      try {
        const snapshot = await getDocs(
          collection(db, "users", dataUID, "species"),
        );

        const counts = {};

        // Build genus → kingdom lookup once
        const genusToKingdom = {};

        Object.entries(phylaByKingdom).forEach(([kingdom, phyla]) => {
          phyla.forEach((phylum) => {
            const classes = classesByPhylum[phylum.toLowerCase()] || [];

            classes.forEach((cl) => {
              const orders = ordersByClass[cl.toLowerCase()] || [];

              orders.forEach((ord) => {
                const families = familiesByOrder[ord.toLowerCase()] || [];

                families.forEach((fam) => {
                  const genera = generaByFamily[fam.toLowerCase()] || [];

                  genera.forEach((g) => {
                    genusToKingdom[String(g).toLowerCase()] =
                      kingdom.toLowerCase();
                  });
                });
              });
            });
          });
        });

        snapshot.forEach((doc) => {
          const data = doc.data();
          const genus = String(data?.genus || "")
            .trim()
            .toLowerCase();

          if (!genus) return;

          const kingdom = genusToKingdom[genus];

          if (!kingdom) return;

          counts[kingdom] = (counts[kingdom] || 0) + 1;
        });

        if (!cancelled) setSpeciesCounts(counts);
      } catch (err) {
        console.error("Failed to load species counts:", err);
        if (!cancelled) setSpeciesCounts({});
      } finally {
        if (!cancelled) setLoadingCounts(false);
      }
    }

    fetchSpeciesCounts();

    return () => {
      cancelled = true;
    };
  }, [dataUID]);

  const representedKingdoms = Object.values(speciesCounts).filter(
    (count) => count > 0,
  ).length;
  const hasSpecies = representedKingdoms > 0;

  return (
    <div className="bioindex-explore-page">
      <BioIndexExploreNavigation uid={uid} />

      <main className="bioindex-explore-main">
        <header className="bioindex-explore-intro">
          <div className="bioindex-explore-intro-text">
            <span className="bioindex-explore-eyebrow">
              {isOwnCollection ? "Your collection" : "Visiting a collection"}
            </span>
            <h1>{MODE_LABELS[browseMode] || "Explore Taxonomy"}</h1>
          </div>
        </header>

        <section
          ref={stepperPanelRef}
          className="bioindex-explore-stepper-panel"
          aria-label="Taxonomy navigation and browsing modes"
        >
          <TaxonomyStepper
            params={{}}
            currentLevel="kingdom"
            uid={uid}
            browseMode={browseMode}
            setBrowseMode={setBrowseMode}
          />
          <div className="bioindex-explore-stepper-hint sm:hidden">
            <span>Swipe to see Family and Genus</span>
            <button
              type="button"
              aria-label="Scroll taxonomy levels to the right"
              onClick={() => {
                stepperPanelRef.current
                  ?.querySelector(".overflow-x-auto")
                  ?.scrollBy({ left: 220, behavior: "smooth" });
              }}
            >
              <span aria-hidden="true">›</span>
            </button>
          </div>
        </section>

        {browseMode === "taxonomy" ? (
          <section
            className="bioindex-explore-kingdoms"
            aria-label="Documented kingdoms"
          >
            {loadingCounts ? (
              <div className="bioindex-explore-empty" role="status">
                <span className="bioindex-explore-spinner" aria-hidden="true" />
                <h2>Loading kingdoms</h2>
              </div>
            ) : !hasSpecies ? (
              <div className="bioindex-explore-empty">
                <h2>No kingdoms to explore yet</h2>
                <p>
                  {isOwnCollection
                    ? "Add your first observation to start exploring your collection."
                    : "This collection has no documented species yet."}
                </p>
                {isOwnCollection && (
                  <Link to="/upload" className="bioindex-explore-empty-action">
                    Add Photos
                  </Link>
                )}
              </div>
            ) : (
              <div className="bioindex-explore-kingdom-grid">
                {kingdoms
                  .filter(
                    (item) => (speciesCounts[item.name.toLowerCase()] || 0) > 0,
                  )
                  .map((kingdom) => {
                    const key = kingdom.name.toLowerCase();
                    const count = speciesCounts[key] || 0;
                    const to = uid
                      ? `/explore/${uid}/kingdom/${key}${adminModeSuffix}`
                      : `/kingdom/${key}`;

                    return (
                      <Link
                        key={kingdom.name}
                        to={to}
                        className="bioindex-explore-kingdom-card"
                      >
                        <span
                          className="bioindex-explore-kingdom-count"
                          aria-label={`${count.toLocaleString()} species`}
                          title={`${count.toLocaleString()} species`}
                        >
                          {count.toLocaleString()}
                        </span>
                        <div className="bioindex-explore-kingdom-illustration">
                          <img
                            src={iconUrl(kingdom.icon)}
                            alt=""
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = defaultImg;
                            }}
                          />
                        </div>
                        <div className="bioindex-explore-kingdom-caption">
                          <h2>{kingdom.name}</h2>
                        </div>
                      </Link>
                    );
                  })}
              </div>
            )}
          </section>
        ) : (
          <section
            className="bioindex-explore-modes-panel"
            aria-label={MODE_LABELS[browseMode]}
          >
            <ExplorePhotoModes
              mode={browseMode}
              dataUID={dataUID}
              routeUID={uid}
              isAdminCollectionMode={adminModeActive}
            />
          </section>
        )}
      </main>

      <footer className="bioindex-explore-footer">
        <p>© {new Date().getFullYear()} BioIndex™. All rights reserved.</p>
        <nav aria-label="Legal and contact links">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/community-guidelines">Guidelines</Link>
          <Link to="/copyright">Copyright</Link>
          <Link to="/contact">Contact</Link>
        </nav>
      </footer>
    </div>
  );
}

export default ExploreSpecies;
